// /admin/ops — Regina's morning glance page.
// Five tiles, in order of "what does she look at first":
// 1) DAU heatmap (12-row grid, today + 6 days back)
// 2) Per-staff activity table with 活跃/沉睡/失联 status
// 3) Notification read-through
// 4) Device split (mobile vs desktop, PWA install signal)
// 5) Frontend errors last 24h
//
// Plus self-health tile showing total events + last_hour count so Regina knows
// telemetry itself is healthy.

import { useState, useEffect } from 'react';
import * as api from '../lib/api.ts';
import { fmtDateTime } from '../lib/dates.ts';
import { isElevated } from '../lib/auth.ts';

export const AdminOps = ({ user }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.loadAdminOps()
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(e => { if (!cancelled) { setErr(e.message || '查询失败'); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  if (!isElevated(user)) {
    return <div className="ops-page"><div className="empty-card">仅管理员 / 店长可访问</div></div>;
  }
  if (loading) return <div className="ops-page"><div className="empty-card">加载中…</div></div>;
  if (err) {
    return (
      <div className="ops-page">
        <div className="empty-card">
          查询失败：{err}
          <button className="btn-secondary" style={{ marginTop: 12 }} onClick={() => window.location.reload()}>
            刷新重试
          </button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="ops-page">
      <header className="ops-header">
        <h1>运营</h1>
        <div className="ops-sub">过去 7 天 · 数据由 events + sessions + tasks 综合</div>
      </header>

      {/* 1. DAU heatmap */}
      <section className="ops-section">
        <h2>每日活跃 (7 天)</h2>
        <Heatmap heatmap={data.dau_heatmap} days={data.days} />
      </section>

      {/* 2. Per-staff activity */}
      <section className="ops-section">
        <h2>员工活跃情况</h2>
        <PerStaffTable rows={data.per_staff} />
      </section>

      {/* 3. Notifications */}
      <section className="ops-section ops-row">
        <NotifTile n={data.notifications} />
        <DeviceTile d={data.device_split} />
        <HealthTile h={data.telemetry_health} />
      </section>

      {/* 4. Frontend errors */}
      <section className="ops-section">
        <h2>前端错误 (24h)</h2>
        <ErrorPanel e={data.errors_24h} />
      </section>
    </div>
  );
};

const Heatmap = ({ heatmap, days }) => {
  if (!heatmap || heatmap.length === 0) {
    return <div className="empty-card">暂无数据，等大家用起来</div>;
  }
  // Day labels: show only weekday + month/day for the 7 columns
  const labels = days.map(d => {
    const dt = new Date(d + 'T00:00:00');
    const wd = ['日','一','二','三','四','五','六'][dt.getDay()];
    return `${dt.getMonth()+1}/${dt.getDate()} ${wd}`;
  });
  return (
    <div className="heatmap">
      <table>
        <thead>
          <tr>
            <th />
            {labels.map((l, i) => <th key={i} className="heatmap-col-head">{l}</th>)}
          </tr>
        </thead>
        <tbody>
          {heatmap.map(row => (
            <tr key={row.staff_id}>
              <th className="heatmap-name">{row.name}</th>
              {row.days.map((active, i) => (
                <td key={i}>
                  <div className={`heatmap-cell ${active ? 'on' : 'off'}`} aria-label={active ? '活跃' : '无活动'} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PerStaffTable = ({ rows }) => {
  if (!rows || rows.length === 0) {
    return <div className="empty-card">暂无员工数据</div>;
  }
  return (
    <table className="staff-table">
      <thead>
        <tr>
          <th>员工</th>
          <th className="num">最近登录</th>
          <th className="num">本周开待办</th>
          <th className="num">本周完成</th>
          <th className="num">本周评论</th>
          <th>状态</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.staff_id} onClick={() => { window.location.hash = `#/people/${r.staff_id}`; }} style={{ cursor: 'pointer' }}>
            <td>
              <div className="staff-name">{r.name}</div>
              <div className="staff-title">{r.title}</div>
            </td>
            <td className="num mono">{r.last_login ? fmtDateTime(r.last_login) : '—'}</td>
            <td className="num mono">{r.tasks_created_7d}</td>
            <td className="num mono">{r.tasks_completed_7d}</td>
            <td className="num mono">{r.comments_7d}</td>
            <td><StatusBadge status={r.status} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const StatusBadge = ({ status }) => {
  const cls = status === '活跃' ? 'badge-active' : status === '沉睡' ? 'badge-dormant' : 'badge-dead';
  return <span className={`status-badge ${cls}`}>{status}</span>;
};

const NotifTile = ({ n }) => (
  <div className="ops-tile">
    <h3>通知读取</h3>
    <div className="tile-big">
      {n.read_rate !== null ? Math.round(n.read_rate * 100) + '%' : '—'}
    </div>
    <div className="tile-sub">
      {n.read_7d} / {n.sent_7d} · 中位响应{' '}
      {n.median_minutes_to_read !== null ? Math.round(n.median_minutes_to_read) + ' 分' : '—'}
    </div>
  </div>
);

const DeviceTile = ({ d }) => {
  const total = d.mobile + d.desktop;
  return (
    <div className="ops-tile">
      <h3>设备分布 (7天)</h3>
      <div className="tile-big">
        {total > 0 ? Math.round((d.mobile / total) * 100) + '%' : '—'}
      </div>
      <div className="tile-sub">手机 · PWA {d.pwa} 次</div>
    </div>
  );
};

const HealthTile = ({ h }) => (
  <div className="ops-tile">
    <h3>遥测健康</h3>
    <div className="tile-big mono">{h.last_hour}</div>
    <div className="tile-sub">最近 1 小时 · 累计 {h.total.toLocaleString()}</div>
  </div>
);

const ErrorPanel = ({ e }) => {
  if (e.count === 0) {
    return <div className="empty-card">没有错误，干净 ✓</div>;
  }
  return (
    <div>
      <div className="errors-count">共 {e.count} 个错误</div>
      <ul className="errors-list">
        {e.recent.map(err => (
          <li key={err.id}>
            <div className="error-msg mono">{err.error_msg || '(empty)'}</div>
            <div className="error-meta mono">{err.actor_id} · {err.path || '/'} · {fmtDateTime(err.at)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
};
